/**
 * Utilidad para gestionar actualizaciones de la aplicación
 */

class UpdateManager {
  constructor() {
    // Identificador único por build (fecha+hora al segundo + hash de commit). Cae a BUILDDATE
    // por compatibilidad si algún bundle antiguo todavía no trae BUILD_ID.
    this.currentVersion = process.env.BUILD_ID || process.env.BUILDDATE;
    this.storageKey = 'simecal_app_version';
    this.checkInterval = 5 * 60 * 1000; // Verificar cada 5 minutos
    this.checkIntervalId = null;
    this.lastCheckAt = 0;
    this.minCheckSpacing = 30 * 1000; // antirrebote: ignora chequeos por foco a menos de 30s del anterior
  }

  /**
   * Inicializa el gestor de actualizaciones
   */
  init() {
    // Service Worker deshabilitado - causa problemas con caché en algunos navegadores
    // La detección de versión funciona perfectamente sin él mediante version.json
    
    // Verificar versión almacenada
    this.checkVersion();

    // Verificar actualizaciones periódicamente (solo en producción web)
    if (process.env.TARGET === 'web' && process.env.NODE_ENV === 'production') {
      this.startPeriodicCheck();
      this.registerActivityListeners();
    }
  }

  /**
   * Comprueba actualizaciones cuando la pestaña recupera foco/visibilidad o vuelve la conexión.
   * Cubre el hueco del polling: una pestaña en segundo plano tiene el setInterval estrangulado
   * por el navegador, así que al volver el usuario forzamos una comprobación inmediata.
   */
  registerActivityListeners() {
    this._onVisible = () => {
      if (document.visibilityState === 'visible') this.checkForUpdatesThrottled();
    };
    this._onFocusOrOnline = () => this.checkForUpdatesThrottled();

    document.addEventListener('visibilitychange', this._onVisible);
    window.addEventListener('focus', this._onFocusOrOnline);
    window.addEventListener('online', this._onFocusOrOnline);
  }

  /**
   * Como checkForUpdates pero ignora llamadas muy seguidas. Al cambiar de pestaña se disparan
   * a la vez visibilitychange y focus; el antirrebote evita el chequeo doble.
   */
  checkForUpdatesThrottled() {
    if (Date.now() - this.lastCheckAt < this.minCheckSpacing) return;
    this.checkForUpdates();
  }

  /**
   * Registra el Service Worker
   */
  async registerServiceWorker() {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      console.log('[UpdateManager] Service Worker registrado');

      // Detectar actualizaciones del Service Worker
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nueva versión disponible
            this.notifyUpdate(newWorker);
          }
        });
      });

      // Verificar actualizaciones manualmente
      registration.update().catch(err => {
        console.debug('[UpdateManager] Error updating SW:', err.message);
      });
    } catch (error) {
      // Service Worker es opcional, no es crítico si falla
      console.debug('[UpdateManager] Service Worker no disponible (normal en desarrollo):', error.message);
    }
  }

  /**
   * Verifica si hay una nueva versión
   */
  checkVersion() {
    const storedVersion = localStorage.getItem(this.storageKey);
    
    if (storedVersion && storedVersion !== this.currentVersion) {
      console.log('[UpdateManager] Nueva versión detectada:', this.currentVersion, '(anterior:', storedVersion + ')');
      this.forceUpdate();
    } else {
      // Guardar versión actual
      localStorage.setItem(this.storageKey, this.currentVersion);
    }
  }

  /**
   * Fuerza la recarga de la aplicación
   */
  forceUpdate() {
    // Limpiar toda la caché del navegador
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Limpiar localStorage de recursos cacheados (si aplica)
    // Mantener solo datos de usuario esenciales
    const userDataKeys = ['user', 'token', 'session'];
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      if (!userDataKeys.some(uk => key.includes(uk))) {
        localStorage.removeItem(key);
      }
    });

    // Guardar nueva versión
    localStorage.setItem(this.storageKey, this.currentVersion);

    // Recargar la página sin caché
    console.log('[UpdateManager] Forzando actualización...');
    window.location.reload(true);
  }

  /**
   * Notifica al usuario que hay una actualización disponible
   */
  notifyUpdate(newWorker) {
    // Usar Framework7 para mostrar notificación si está disponible
    if (window.f7) {
      const app = window.f7;
      
      app.dialog.confirm(
        'Hay una nueva versión disponible. ¿Desea actualizar ahora?',
        'Actualización disponible',
        () => {
          // Usuario acepta actualizar
          if (newWorker) {
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
          this.forceUpdate();
        },
        () => {
          // Usuario rechaza, pero se actualizará en el próximo reload
          console.log('[UpdateManager] Actualización pospuesta por el usuario');
        }
      );
    } else {
      // Fallback sin Framework7
      if (confirm('Hay una nueva versión disponible. ¿Desea actualizar ahora?')) {
        if (newWorker) {
          newWorker.postMessage({ type: 'SKIP_WAITING' });
        }
        this.forceUpdate();
      }
    }
  }

  /**
   * Inicia verificación periódica de actualizaciones
   */
  startPeriodicCheck() {
    if (this.checkIntervalId) clearInterval(this.checkIntervalId);
    this.checkIntervalId = setInterval(() => {
      this.checkForUpdates();
    }, this.checkInterval);
  }

  /**
   * Verifica si hay actualizaciones disponibles en el servidor
   */
  async checkForUpdates() {
    this.lastCheckAt = Date.now(); // registra el chequeo (polling o foco) para el antirrebote
    try {
      // Agregar timestamp para evitar caché del navegador
      const timestamp = new Date().getTime();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.version) {
          console.log('[UpdateManager] Versión servidor:', data.version);
          console.log('[UpdateManager] Versión local:', this.currentVersion);
          
          if (data.version !== this.currentVersion) {
            console.log('[UpdateManager] Nueva versión disponible en servidor:', data.version);
            this.notifyUpdate();
          }
        } else {
          console.warn('[UpdateManager] version.json no tiene propiedad "version"');
        }
      } else {
        console.warn('[UpdateManager] No se pudo descargar version.json. Status:', response.status);
      }
    } catch (error) {
      // Silenciar errores de red
      console.debug('[UpdateManager] No se pudo verificar actualizaciones:', error.message);
    }
  }

  /**
   * Limpia todos los datos de caché (útil para debugging)
   */
  clearAllCache() {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    
    localStorage.removeItem(this.storageKey);
    console.log('[UpdateManager] Toda la caché ha sido limpiada');
  }
}

export default new UpdateManager();
